import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { ConfigProvider } from "antd";
import { useTheme } from "../../../context/ThemeContext";
import { getAntdThemeConfig } from "../../../utils/antdTheme";
import {
  MainContainer,
  OverviewSection,
  SectionTitle,
  InfoTilesGrid,
  FullWidthSpace,
} from "./styles";
import DisputesHeader from "./DisputesHeader";
import InfoTile from "./InfoTile";
import DisputesCarousel from "./DisputesCarousel";
import DisputeDetailsModal from "./DisputeDetailsModal";
import { DisputeData, Dispute } from "./types";
import { fetchAnalyticsDisputes } from "../../DisputeManagement/store/disputesSlice";
import type { AppDispatch, RootState } from "../../../redux/store";

const Disputes: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { analyticsData, analyticsLoading } = useSelector(
    (state: RootState) => state.disputes
  );
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  useEffect(() => {
    dispatch(fetchAnalyticsDisputes());
  }, [dispatch]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDispute(null);
  };

  const handleSetSelectedDispute = (dispute: Dispute) => {
    setSelectedDispute(dispute);
  };

  const chartColor1 = isDarkMode ? "#c2c2c2" : "#666666";
  const chartColor2 = isDarkMode ? "#7d7d7d" : "#2e2e2e";
  const chartColorHover1 = isDarkMode ? "#e0e0e0" : "#c2c2c2";
  const chartColorHover2 = isDarkMode ? "#e0e0e0" : "#e0e0e0";

  const defaultDisputeData: DisputeData = {
    totalDisputes: 0,
    resolvedDisputes: 0,
    pendingDisputes: 0,
    disputes: [],
  };

  const displayData: DisputeData = analyticsData || defaultDisputeData;
  const loading = analyticsLoading;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <MainContainer>
          <FullWidthSpace direction="vertical" size="large">
            <DisputesHeader disputeData={displayData} />

            <OverviewSection>
              <SectionTitle level={4}>Overview</SectionTitle>
              <InfoTilesGrid>
                <InfoTile
                  title="Total Disputes"
                  value={displayData.totalDisputes || 0}
                  description="Total number of disputes received."
                  iconColor="#E53E3E"
                  chartData={{
                    labels: ["Resolved", "Pending"],
                    data: [
                      displayData.resolvedDisputes || 0,
                      displayData.pendingDisputes || 0,
                    ],
                    backgroundColor: ["#38A169", "#E53E3E"],
                    hoverBackgroundColor: ["#2F855A", "#C53030"],
                  }}
                  showPercentage={true}
                  loading={loading}
                />
                <InfoTile
                  title="Resolved Disputes"
                  value={displayData.resolvedDisputes || 0}
                  description="Number of disputes that have been resolved."
                  iconColor="#38A169"
                  chartData={{
                    labels: ["Resolved", "Pending"],
                    data: [
                      displayData.resolvedDisputes || 0,
                      (displayData.totalDisputes || 0) - (displayData.resolvedDisputes || 0),
                    ],
                    backgroundColor: [chartColor1, chartColor2],
                    hoverBackgroundColor: [chartColorHover1, chartColorHover2],
                  }}
                  loading={loading}
                />
                <InfoTile
                  title="Pending Disputes"
                  value={displayData.pendingDisputes || 0}
                  description="Number of disputes awaiting resolution."
                  iconColor="#DD6B20"
                  chartData={{
                    labels: ["Pending", "Resolved"],
                    data: [
                      displayData.pendingDisputes || 0,
                      (displayData.totalDisputes || 0) - (displayData.pendingDisputes || 0),
                    ],
                    backgroundColor: [chartColor1, chartColor2],
                    hoverBackgroundColor: [chartColorHover1, chartColorHover2],
                  }}
                  loading={loading}
                />
              </InfoTilesGrid>
            </OverviewSection>

            <DisputesCarousel
              disputes={displayData.disputes || []}
              onOpen={handleOpenModal}
              setSelectedDispute={handleSetSelectedDispute}
            />

            {selectedDispute && (
              <DisputeDetailsModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                dispute={selectedDispute}
              />
            )}
          </FullWidthSpace>
        </MainContainer>
      </ConfigProvider>
    </StyledThemeProvider>
  );
};

export default Disputes;
